import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IApiRequest,
  IBidRequirement,
  IBidResponse,
  PlanCountType,
  PlanStatus,
} from "@miplatform/types";

export const useBids = () => {
  const [bids, setBids] = useState<IBidResponse[]>([]);
  const [bidDetails, setBidDetails] = useState<IBidResponse | null>(null);
  const [areaTemplates, setAreaTemplates] = useState<IBidResponse[]>([]);
  const [availableRequirements, setAvailableRequirements] = useState<
    IBidRequirement[]
  >([]);
  const [refresh, setRefresh] = useState(false);

  const stateManager = useComponentState();

  const getBids = ({ top, skip, filters, sort = "name" }: IApiRequest = {}) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.getAll({
        filters,
        sort,
        top,
        skip,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setBids(response.data);
      }
      return response;
    });
  };

  const getPlanCounts = ({
    planCountType,
    planStatus,
  }: {
    planCountType: PlanCountType;
    planStatus?: PlanStatus;
  }) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.getCounts({
        planCountType,
        planStatus,
      });
      return response;
    });
  };

  const getBidDetails = (id: string) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.getById({ id });
      if (response.status === HTTP_STATUSES.OK) {
        setBidDetails(response.data);
      }
      return response;
    });
  };

  const updateBid = async (
    data: Partial<IBidResponse>,
    successText?: string
  ) => {
    return stateManager.run(async () => {
      return homeService.plans.update({
        data,
        toastMessageConfig: {
          success: {
            text: successText || `${data.name} was created successfully.`,
          },
        },
      });
    });
  };

  const assignBid = async (bid: IBidResponse, assigneeId: string | null) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.assign({
        id: bid.id,
        data: { assigneeId, modifiedOn: bid.modifiedOn },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
      return response;
    });
  };

  const updateBidStatus = async (bid: IBidResponse, status: PlanStatus) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.updateStatus({
        id: bid.id,
        data: { planStatus: status, modifiedOn: bid.modifiedOn },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
      return response;
    });
  };

  const deleteBid = async (data: IBidResponse) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.delete({
        data,
        toastMessageConfig: {
          success: {
            text: `${data.name} was deleted successfully.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
    });
  };

  const getAreaTemplates = ({
    top,
    skip,
    filters,
    sort = "name",
    search = "",
  }: IApiRequest = {}) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.templates.getAll({
        top,
        skip,
        sort,
        filters: {
          ...filters,
          ...(search && { name: { contains: search } }),
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAreaTemplates(response.data);
      }
      return response;
    });
  };

  const createAreaTemplate = async (
    data: Partial<IBidResponse>,
    successText?: string
  ) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.templates.createAreaTemplate({
        data,
        toastMessageConfig: {
          success: {
            text:
              successText || `Successfully created Area Template ${data.name}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
      return response;
    });
  };

  const getAvailableRequirements = (planId: string) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.requirements.getAvailable({
        planId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAvailableRequirements(response.data);
      }
      return response;
    });
  };

  const updatePlanRequirements = (planId: string, requirementIds: string[]) => {
    return stateManager.run(async () => {
      return homeService.plans.requirements.update({
        planId,
        data: { requirementIds },
        toastMessageConfig: {
          failure: { text: "Failed to save Requirements." },
        },
      });
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    refresh,
    bids,
    setBids,
    getBids,
    getPlanCounts,
    bidDetails,
    setBidDetails,
    getBidDetails,
    updateBid,
    deleteBid,
    assignBid,
    updateBidStatus,
    areaTemplates,
    setAreaTemplates,
    getAreaTemplates,
    createAreaTemplate,
    availableRequirements,
    setAvailableRequirements,
    getAvailableRequirements,
    updatePlanRequirements,
  };
};
